import { useContext, useMemo, useState } from 'react'

import {
  getCollectionDiscounts,
  getProductDiscounts,
} from '@lib/discount/product'
import { LanguageContext } from '@lib/language-context'
import { ProductContext } from '@lib/product/context'
import {
  getDocumentPercentDiscount,
  getProductPrices,
} from '@lib/product/price'
import { getPageUrl } from '@lib/routes'
import { UserContext } from '@lib/user/context'
import {
  getHasUserPurchasePermission,
  getHasUserViewPricePermission,
} from '@lib/user/helpers'

import ProductActions from '@blocks/product/product-actions'
import ProductActionsGuest from '@blocks/product/product-actions-guest'
import ProductGallery from '@blocks/product/product-gallery'
import ProductPrice from '@blocks/product/product-price'
import ProductPriceRequestForm from '@blocks/product/product-price-request-form'
import SimplePortableText from '@components/simple-portable-text'
import ProductOptionsSelect from '@blocks/product/product-options-select'
import StickyCTA from '@components/sticky-add-to-cart'

const ProductHero = () => {
  const { locale } = useContext(LanguageContext)
  const { product, activeVariant, onVariantChange } = useContext(ProductContext)
  const { user } = useContext(UserContext)

  const [previewVariantId, setPreviewVariantId] = useState<number | null>(null)

  const previewVariant = useMemo(
    () =>
      product?.variants?.find(
        (variant) => variant.variantID === previewVariantId
      ),
    [previewVariantId, product?.variants]
  )

  const [price, comparePrice] = useMemo(() => {
    if (product?.productType === 'd2cPriceOnRequest') {
      return [,] as const
    }

    const productDiscounts = getProductDiscounts(
      user?.company?.productDiscounts
    )
    const collectionDiscounts = getCollectionDiscounts(
      user?.company?.collectionDiscounts
    )
    const productPercentDiscount = getDocumentPercentDiscount(
      productDiscounts ?? [],
      'product',
      product?._id ? [product._id] : []
    )
    const collectionPercentDiscount = getDocumentPercentDiscount(
      collectionDiscounts ?? [],
      'collection',
      product?.collectionIds ?? []
    )

    return getProductPrices({
      productPrice: product?.price,
      productComparePrice: product?.comparePrice,
      variantPrice: activeVariant?.price,
      variantComparePrice: activeVariant?.comparePrice,
      productPercentDiscount,
      collectionPercentDiscount,
      companyPercentDiscount: user?.company?.percentDiscount,
    })
  }, [activeVariant, product, user])

  const hasUserPurchasePermission = useMemo(() => {
    if (!product) {
      return false
    }

    return getHasUserPurchasePermission(product.productType, user)
  }, [product, user])

  const hasUserViewPricePermission = useMemo(() => {
    if (!product) {
      return false
    }

    return getHasUserViewPricePermission(product.productType, user)
  }, [product, user])

  const productPageUrl = useMemo(() => {
    if (!product) {
      return
    }

    return getPageUrl({
      locale,
      pageType: 'product',
      slugs: product.slug.current,
    })
  }, [locale, product])

  if (!product || !activeVariant || !onVariantChange) {
    return null
  }

  const isPriceOnRequest = product.productType === 'd2cPriceOnRequest'
  const isUserLoading = typeof user === 'undefined'

  const hasPrice =
    typeof price !== 'undefined' &&
    !isPriceOnRequest &&
    !isUserLoading &&
    hasUserViewPricePermission
  const hasActions =
    !isPriceOnRequest && !isUserLoading && hasUserPurchasePermission
  const hasGuestActions =
    !isPriceOnRequest && !isUserLoading && !hasUserPurchasePermission

  return (
    <>
      <section className="container grid grid-cols-1 md:grid-cols-2 gap-8 py-12">
        <ProductGallery
          photosets={product.galleryPhotos ?? []}
          activeVariant={activeVariant}
          previewVariant={previewVariant}
          preloadFirstPhoto
          hasDrag
          hasThumbnails
          hasArrows={true}
        />

        <div className="flex flex-col w-full md:max-w-[620px] md:ml-auto gap-y-[1.875rem]">
          <div className="space-y-2.5">
            <h1 className="is-h2">{product.title}</h1>
            {hasPrice && (
              <ProductPrice
                size="lg"
                price={price}
                comparePrice={comparePrice}
                showPrice
                showOldPrice
                showCurrency
                showPriceTax
              />
            )}
          </div>

          <ProductOptionsSelect
            product={product}
            activeVariant={activeVariant}
            onChange={(variant) => onVariantChange(variant.variantID)}
            onPreview={(variant) =>
              setPreviewVariantId(variant?.variantID ?? null)
            }
          />

          {hasActions && (
            <ProductActions
              product={product}
              variantIds={[activeVariant.variantID]}
              inStock={activeVariant.inStock}
            />
          )}

          {hasGuestActions && <ProductActionsGuest returnTo={productPageUrl} />}

          {!!product.description && (
            <SimplePortableText className="rc" content={product.description} />
          )}

          {isPriceOnRequest && (
            <ProductPriceRequestForm
              activeVariant={activeVariant}
              className="pt-8"
            />
          )}
        </div>
      </section>

      {product.inStock && hasActions && (
        <StickyCTA
          hasProductPrice={hasPrice}
          productPrice={price}
          product={product}
          variantIds={[activeVariant.variantID]}
          productImage={product.galleryPhotos?.[0].photos?.[0]}
          activeVariant={activeVariant}
        />
      )}
    </>
  )
}

export default ProductHero
